// Audio-Engine: Kanalzüge, Master-Bus, Wiedergabe (Audio-Clips + MIDI mit Lookahead-Scheduler),
// Metronom und Pegel. Die Engine liest das Projekt nur – Änderungen laufen immer über den Store.
import type { Project, Track, Clip, InsertSlot } from '@srs/shared';
import { useStudio } from '../state/store.ts';
import { voiceFor } from './instruments.ts';
import { peaks } from './waveform.ts';
import { MixGraph } from './mixgraph.ts';
import { audioSegments, applyEnvelope, midiEvents } from './segments.ts';
import { clickTimes } from '@srs/shared';
import { elasticReady, onElasticChange } from './elastic.ts';
import { isFrozen } from '../edit/mixops.ts';
import { fx, fxActive, rendered as fxRendered } from './fxstate.ts';
import { bussesOf } from './plugins.ts';
import type { AutoPoint } from '@srs/shared';
import { clampV, insOverrides, isAutomated, isInsParam, masterTrack, points as autoPoints, staticValue, trackLike, valueAt, writePass } from '../edit/automation.ts';

/** statischen Wert setzen (Fader, Pan, Mute, Send), damit ein angefasster Regler am Kurvenwert beginnt */
function setStatic(t: Track, param: string, v: number) {
  if (param === 'volume') t.volumeDb = Math.round(v * 10) / 10;
  else if (param === 'pan') t.pan = v;
  else if (param === 'mute') t.mute = v >= 0.5;
  else if (isInsParam(param)) { const [, slotId, key] = param.split(':'); const s = t.inserts.find((x) => x.id === slotId); if (s) s.params = { ...s.params, [key]: v }; }
  else if (param.startsWith('send:')) { const l = v <= -59.9 ? 0 : Math.pow(10, v / 20); const x = t.sends.find((s) => s.busId === param.slice(5)); if (x) x.level = l; else t.sends.push({ busId: param.slice(5), level: l }); }
}

export { dbToGain } from './mixgraph.ts';

/** Parameter eines Inserts lesen (Auto-Mix-Anzeige) */
export const insertParam = (t: Track, role: string, key: string, fallback: number) =>
  t.inserts.find((s: InsertSlot) => s.role === role && !s.bypass)?.params[key] ?? fallback;

class Engine {
  ctx: AudioContext | null = null;
  graph: MixGraph | null = null;
  buffers = new Map<string, AudioBuffer>();
  private playing: AudioScheduledSourceNode[] = [];
  private timer: number | null = null;
  private startCtx = 0; private startPos = 0; private scheduledUntil = 0;
  monitor = { phone: false, mono: false };
  /** A/B-Vergleich: wenn gesetzt, klingt dieser Mix-Stand statt des aktuellen (Pegel, Inserts, Sends, Master; nicht die Noten) */
  private compare: Project | null = null;
  setCompare(p: Project | null) { this.compare = p; this.sync(useStudio.getState().project); }
  get comparing() { return !!this.compare; }
  private starting: Promise<AudioContext> | null = null;

  async ensure(): Promise<AudioContext> {
    if (this.ctx && this.graph) { if (this.ctx.state === 'suspended') await this.ctx.resume(); return this.ctx; }
    this.starting ??= (async () => {
      const ctx = new AudioContext({ latencyHint: 'interactive', sampleRate: 48000 });
      const graph = new MixGraph(ctx, true);
      await graph.init();
      await ctx.audioWorklet.addModule('/recorder-worklet.js').catch(() => undefined);
      this.ctx = ctx; this.graph = graph;
      this.sync(useStudio.getState().project);
      return ctx;
    })();
    return this.starting;
  }

  private strip(t: Track) { return this.graph!.strip(t.id); }

  /** Projektzustand auf die Audio-Knoten übertragen */
  sync(current: Project) {
    if (!this.graph) return;
    const base = this.compare;
    const p: Project = base
      ? { ...current, masterDb: base.masterDb, mixBus: base.mixBus, masterInserts: base.masterInserts, busses: base.busses, tracks: current.tracks.map((t) => base.tracks.find((b) => b.id === t.id) ?? t) }
      : current;
    this.graph.sync(p, this.monitor, { playing: useStudio.getState().transport.playing, pos: this.position, manual: this.manual });
  }

  // ---- Automation schreiben (Touch, Latch, Write) ----
  /** Parameter, die gerade die Hand steuert: `${trackId}|${param}` */
  manual = new Set<string>();
  private rec = new Map<string, AutoPoint[]>();
  private latched = new Set<string>();
  private autoMode = (trackId: string) => trackLike(useStudio.getState().project, trackId)?.automation?.mode ?? 'off';
  /** Regler angefasst oder losgelassen. In Touch/Latch/Write wird während der Wiedergabe aufgezeichnet. */
  touch(trackId: string, param: string, on: boolean) {
    const st = useStudio.getState(), mode = this.autoMode(trackId), key = `${trackId}|${param}`;
    if (!st.transport.playing || !['touch', 'latch', 'write'].includes(mode)) return;
    if (on) {
      if (this.manual.has(key)) return;
      // Regler übernimmt den aktuellen Kurvenwert, damit nichts springt
      const t = trackLike(st.project, trackId)!;
      if (isAutomated(t, param)) {
        const v = clampV(param, valueAt(autoPoints(t, param), this.position, param, 0), t);
        st.commit('Fader', (p) => { const x = trackLike(p, trackId)!; setStatic(x, param, v); }, { merge: true });
      }
      this.manual.add(key);
      if (mode === 'latch') this.latched.add(key);
      if (!this.rec.has(key)) this.rec.set(key, []);
      this.sync(useStudio.getState().project);
    } else if (mode === 'touch' && this.manual.has(key)) {
      this.commitPasses([key]);
      this.manual.delete(key);
      this.sync(useStudio.getState().project);
      this.scheduleAutomationNow();
    }
  }
  /** Aufnahme in die Kurven übernehmen (ein Rückgängig-Schritt) */
  private commitPasses(keys = [...this.rec.keys()]) {
    const list = keys.map((k) => [k, this.rec.get(k) ?? []] as const).filter(([, pts]) => pts.length > 1);
    for (const k of keys) this.rec.delete(k);
    if (!list.length) return;
    useStudio.getState().commit('Automation schreiben', (p) => {
      for (const [k, pts] of list) {
        const [tid, param] = k.split('|');
        const t = trackLike(p, tid);
        if (t) writePass(t, param, pts);
      }
    });
  }
  private recordTick() {
    if (!this.manual.size) return;
    const p = useStudio.getState().project, pos = this.position;
    for (const key of this.manual) {
      const [tid, param] = key.split('|');
      const t = trackLike(p, tid);
      if (!t) continue;
      const arr = this.rec.get(key) ?? [];
      arr.push({ t: Math.max(0, pos), v: staticValue(t, param) });
      this.rec.set(key, arr);
    }
  }
  private scheduleAutomationNow() {
    if (!this.graph || !useStudio.getState().transport.playing) return;
    this.graph.scheduleAutomation(useStudio.getState().project, this.position + 0.02, (x) => this.ctxTime(x), this.manual);
  }
  /** Scrubben: kurze Ausschnitte an der Mausposition hörbar machen (Scrubber-Werkzeug, Kap. 27) */
  private lastScrub = 0;
  async scrub(trackIds: string[], at: number) {
    if (useStudio.getState().transport.playing) return;
    const now = performance.now();
    if (now - this.lastScrub < 35) return;
    this.lastScrub = now;
    const ctx = await this.ensure(), p = this.withOverride(useStudio.getState().project), len = 0.09;
    for (const tid of trackIds) {
      const t = p.tracks.find((x) => x.id === tid);
      if (!t) continue;
      const input = this.graph!.strip(tid).input;
      for (const seg of audioSegments(p, tid)) {
        if (at < seg.from || at >= seg.to) continue;
        const buf = this.buffers.get(seg.sourceId ?? seg.clip.sourceId!);
        if (!buf) continue;
        const src = ctx.createBufferSource(); src.buffer = buf;
        const g = ctx.createGain(), t0 = ctx.currentTime + 0.005;
        g.gain.setValueAtTime(0, t0); g.gain.linearRampToValueAtTime(1, t0 + 0.008); g.gain.setValueAtTime(1, t0 + len - 0.012); g.gain.linearRampToValueAtTime(0, t0 + len);
        src.connect(g).connect(input);
        src.start(t0, seg.src + (at - seg.from), len);
      }
      const voice = voiceFor(t.instrument?.plugin, t.instrument?.preset);
      for (const c of p.clips) if (c.trackId === tid && c.kind === 'midi') for (const e of midiEvents(p, c, at, at + len)) voice(ctx, input, e.n.m, e.n.v, ctx.currentTime + 0.005 + (e.at - at), Math.min(0.15, e.dur));
    }
    useStudio.getState().setTransport({ position: Math.max(0, at) });
  }

  /** Nach Änderungen an Kurven während der Wiedergabe neu einplanen */
  rescheduleAutomation() { this.sync(useStudio.getState().project); this.scheduleAutomationNow(); }

  setMonitor(m: Partial<Engine['monitor']>) { Object.assign(this.monitor, m); this.sync(useStudio.getState().project); }

  get position() {
    if (!this.ctx || !useStudio.getState().transport.playing) return useStudio.getState().transport.position;
    return this.posAtCtx(this.ctx.currentTime);
  }
  /** Abschnitte der laufenden Wiedergabe: ab Kontextzeit ctx wird Projektposition pos gespielt (ein Eintrag je Loop-Durchgang) */
  segments: { ctx: number; pos: number }[] = [];
  /** Projektposition, die zur Kontextzeit t gerendert wird */
  posAtCtx(t: number) {
    let s = this.segments[0] ?? { ctx: this.startCtx, pos: this.startPos };
    for (const x of this.segments) if (x.ctx <= t + 1e-9) s = x;
    return s.pos + (t - s.ctx);
  }
  /** Loop aktiv? Wiedergabe immer, Aufnahme nur im Loop-Recording */
  private looping(p: Project) {
    const st = useStudio.getState().transport;
    return p.loop.enabled && p.loop.end - p.loop.start > 0.05 && !this.until && (!st.recording || !!st.loopRecord || this.loopRecording);
  }
  /** Vom Recorder gesetzt, solange eine Loop-Aufnahme läuft (auch wenn sie über den Loop-Schalter statt ⌥L kam) */
  loopRecording = false;

  /** Ende einer abgespielten Auswahl: danach stoppt die Wiedergabe und springt an den Anfang der Auswahl */
  private until: { end: number; back: number } | null = null;
  /** Comping: statt der Haupt-Playlist einer Spur diese Clips abspielen (Lane-Solo) */
  laneOverride = new Map<string, Clip[]>();
  private withOverride(p: Project): Project {
    if (!this.laneOverride.size) return p;
    return { ...p, clips: [...p.clips.filter((c) => !this.laneOverride.has(c.trackId)), ...[...this.laneOverride.values()].flat()] };
  }

  /** Eigene Plugins live über die Bridge (setzt bridgefx): Start, Stopp, Neuplanung und Vorlauf beim Start */
  live: { start(from: number): void; stop(): void; startDelay(): number } | null = null;
  /** Quelle in die laufende Wiedergabe aufnehmen (wird beim Stopp und Neuplanen beendet) */
  addSource(src: AudioScheduledSourceNode) { this.playing.push(src); }

  async play(from = useStudio.getState().transport.position, until?: number) {
    const ctx = await this.ensure();
    this.until = until !== undefined && until > from ? { end: until, back: from } : null;
    this.stopSources(); this.closeGates();
    this.startCtx = ctx.currentTime + 0.05 + (this.live?.startDelay() ?? 0);
    this.startPos = from;
    this.scheduledUntil = from;
    this.segments = [{ ctx: this.startCtx, pos: from }];
    this.graph?.resetLoudness();
    // Write: Lautstärke und Panorama aller Spuren in Write werden vom Start an geschrieben
    const pj = useStudio.getState().project;
    for (const t of pj.tracks) if (t.automation?.mode === 'write') for (const param of ['volume', 'pan']) { this.manual.add(`${t.id}|${param}`); this.rec.set(`${t.id}|${param}`, []); }
    if (pj.masterAutomation?.mode === 'write') { this.manual.add('master|volume'); this.rec.set('master|volume', []); }
    const p0 = useStudio.getState().project;
    this.scheduleAudio(this.withOverride(p0), from, this.looping(p0) && from < p0.loop.end ? p0.loop.end : Infinity);
    useStudio.getState().setTransport({ playing: true });
    this.sync(useStudio.getState().project);
    this.graph!.scheduleAutomation(useStudio.getState().project, from, (x) => this.ctxTime(x), this.manual);
    this.live?.start(from);
    this.tick();
    this.timer = window.setInterval(() => this.tick(), 25);
    useStudio.getState().setTransport({ playing: true });
  }

  /** Kontextzeit des letzten Stopps (für die Aufnahme: Ende des letzten Durchgangs) */
  lastStopCtx = 0;
  private stopListeners = new Set<() => void>();
  /** Wird nach jedem Stopp aufgerufen (auch beim automatischen Stopp am Ende einer Auswahl oder des Post-Roll) */
  onStop(l: () => void) { this.stopListeners.add(l); return () => { this.stopListeners.delete(l); }; }

  /** MIDI-Noten laufen über ein Gate je Spur: beim Stopp wird es kurz ausgeblendet, damit lange Noten (Pads, Akkorde) nicht nachklingen */
  private gates = new Map<string, GainNode>();
  private gate(trackId: string): GainNode {
    let g = this.gates.get(trackId);
    if (!g) { g = this.ctx!.createGain(); g.connect(this.graph!.strip(trackId).input); this.gates.set(trackId, g); }
    return g;
  }
  private closeGates() {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    for (const g of this.gates.values()) { g.gain.cancelScheduledValues(now); g.gain.setValueAtTime(g.gain.value, now); g.gain.linearRampToValueAtTime(0, now + 0.03); setTimeout(() => g.disconnect(), 120); }
    this.gates.clear();
  }

  stop() {
    const pos = this.position;
    this.closeGates();
    this.loopRecording = false;
    this.lastStopCtx = this.ctx?.currentTime ?? 0;
    this.live?.stop();
    this.stopSources();
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.graph?.cancelAutomation();
    useStudio.getState().setTransport({ playing: false, recording: false, position: Math.max(0, pos) });
    // Aufgezeichnete Automation übernehmen; Write springt danach auf Touch (wie in Pro Tools)
    const wrote = [...this.manual].map((k) => k.split('|')[0]);
    this.commitPasses();
    this.manual.clear(); this.latched.clear();
    const pj = useStudio.getState().project;
    const writeTracks = [...pj.tracks, masterTrack(pj)].filter((t) => t.automation?.mode === 'write' && wrote.includes(t.id)).map((t) => t.id);
    if (writeTracks.length) useStudio.getState().commit('Automation: Touch', (p) => { for (const t of [...p.tracks, masterTrack(p)]) if (writeTracks.includes(t.id) && t.automation) t.automation.mode = 'touch'; });
    this.sync(useStudio.getState().project);
    this.stopListeners.forEach((l) => l());
  }

  locate(sec: number) {
    const playing = useStudio.getState().transport.playing;
    useStudio.getState().setTransport({ position: Math.max(0, sec) });
    if (playing) void this.play(Math.max(0, sec));
  }

  private stopSources() { this.playing.forEach((s) => { try { s.stop(); } catch { /* bereits beendet */ } }); this.playing = []; }

  /** AudioContext-Zeit, zu der die Projektposition gerendert wird (während der Wiedergabe) */
  ctxTimeOf(projectSec: number) { return this.ctxTime(projectSec); }
  private ctxTime(projectSec: number) { return this.startCtx + (projectSec - this.startPos); }

  /** Audio aller Spuren ab `from` einplanen (sichtbare Abschnitte, Clip Gain, Fades, Loops) */
  /** Audio von from bis until (Loop-Ende) einplanen */
  private scheduleAudio(p: Project, from: number, until = Infinity) {
    const ctx = this.ctx!;
    for (const t of p.tracks) {
      if (t.kind === 'bus') continue;
      // Eingefroren: die gerechnete Fassung statt Clips und Instrument
      if (isFrozen(p, t)) {
        const buf = this.buffers.get(t.frozen!.sourceId);
        if (buf && from < Math.min(until, buf.duration)) {
          const src = ctx.createBufferSource(); src.buffer = buf;
          const fg = ctx.createGain(); fg.gain.value = Math.pow(10, (t.frozen!.gainDb ?? 0) / 20);
          src.connect(fg).connect(this.strip(t).input);
          const e0 = Math.min(until, buf.duration);
          src.start(Math.max(ctx.currentTime, this.ctxTime(from)), from, e0 - from);
          this.playing.push(src);
        }
        continue;
      }
      // Eigene Plugins: live von der Bridge (liefert bridgefx) oder die gerechnete Fassung ab 0 s
      if (fxActive(p, t, true)) {
        if (!fx.live.has(t.id)) this.scheduleRendered(t, this.strip(t).input, from, until);
        continue;
      }
      for (const seg of audioSegments(p, t.id)) {
        if (seg.to <= from || seg.from >= until) continue;
        const buf = this.buffers.get(seg.sourceId ?? seg.clip.sourceId!);
        if (!buf) continue;
        const s0 = Math.max(seg.from, from), e0 = Math.min(seg.to, until), srcAt = seg.src + (s0 - seg.from);
        if (srcAt >= buf.duration) continue;
        const src = ctx.createBufferSource();
        src.buffer = buf;
        const g = ctx.createGain();
        src.connect(g).connect(this.strip(t).input);
        applyEnvelope(g, seg.clip, s0, e0, (x) => this.ctxTime(x));
        // am Loop-Ende kurz ausblenden statt hart abschneiden
        if (e0 < seg.to) { const te = this.ctxTime(e0); g.gain.setValueAtTime(g.gain.value, Math.max(ctx.currentTime, te - 0.004)); g.gain.linearRampToValueAtTime(0, te); }
        src.start(Math.max(ctx.currentTime, this.ctxTime(s0)), srcAt, Math.min(e0 - s0, buf.duration - srcAt));
        this.playing.push(src);
      }
    }
    // Busse mit eigenen Plugins: gerechnete Fassung direkt in die Kette (die Summe der Sends ist dann stumm)
    for (const b of bussesOf(p)) if (fxRendered(p, b)) { const ch = this.graph?.channel(b.id); if (ch) this.scheduleRendered(b, ch.chain.input, from, until); }
  }

  /** Gerechnete Fassung (eigene Plugins) ab Projektposition from einplanen */
  private scheduleRendered(t: Track, dest: AudioNode, from: number, until: number) {
    const ctx = this.ctx!, buf = this.buffers.get(t.bridgeRender!.sourceId);
    if (!buf) return;
    const e0 = Math.min(until, buf.duration);
    if (from >= e0) return;
    const src = ctx.createBufferSource(); src.buffer = buf;
    src.connect(dest);
    src.start(Math.max(ctx.currentTime, this.ctxTime(from)), from, e0 - from);
    this.playing.push(src);
  }

  /** Nach einer Änderung während der Wiedergabe: Audio ab der aktuellen Stelle neu einplanen */
  private resched: number | null = null;
  reschedule() {
    if (!useStudio.getState().transport.playing || !this.ctx) return;
    if (this.resched) clearTimeout(this.resched);
    this.resched = window.setTimeout(() => {
      this.resched = null;
      if (!useStudio.getState().transport.playing) return;
      this.stopSources();
      const p = useStudio.getState().project, from = this.position + 0.03;
      this.live?.start(from);
      this.scheduleAudio(this.withOverride(p), from, this.looping(p) && from < p.loop.end ? p.loop.end : Infinity);
      this.scheduleAutomationNow();
    }, 15);
  }

  /** Lookahead-Scheduler für MIDI und Metronom */
  private tick() {
    const ctx = this.ctx!;
    const st = useStudio.getState();
    const p = this.withOverride(st.project);
    this.recordTick();
    this.graph?.applyInsAutomation(p, this.position, this.manual);
    if (this.until && this.position >= this.until.end) {
      const back = this.until.back;
      this.until = null;
      this.stop();
      useStudio.getState().setTransport({ position: back });
      return;
    }
    let horizon = this.startPos + (ctx.currentTime + 0.12 - this.startCtx);
    const from = this.scheduledUntil;
    const loop = this.looping(p) && this.startPos < p.loop.end;
    if (loop && horizon >= p.loop.end) horizon = p.loop.end;
    for (const c of p.clips) {
      if (c.kind !== 'midi' || !c.notes) continue;
      const t = p.tracks.find((x) => x.id === c.trackId);
      if (!t || isFrozen(p, t) || fxActive(p, t, true)) continue;
      const voice = voiceFor(t.instrument?.plugin, t.instrument?.preset);
      for (const e of midiEvents(p, c, from, horizon)) voice(ctx, this.gate(t.id), e.n.m, e.n.v, this.ctxTime(e.at), e.dur);
    }
    if (st.transport.metronome) {
      for (const k of clickTimes(p, from, horizon)) this.click(this.ctxTime(k.t), k.accent);
    }
    this.scheduledUntil = horizon;
    // Lückenloser Loop: nächsten Durchgang genau an das Loop-Ende anschließen
    if (loop && horizon >= p.loop.end) {
      const at = this.ctxTime(p.loop.end);
      this.startCtx = at; this.startPos = p.loop.start; this.scheduledUntil = p.loop.start;
      this.segments.push({ ctx: at, pos: p.loop.start });
      this.scheduleAudio(p, p.loop.start, p.loop.end);
      window.setTimeout(() => { if (useStudio.getState().transport.playing) this.scheduleAutomationNow(); }, Math.max(0, (at - ctx.currentTime) * 1000));
    }
  }

  click(t: number, accent: boolean) {
    const ctx = this.ctx!;
    const o = ctx.createOscillator(), g = ctx.createGain();
    o.frequency.value = accent ? 1760 : 1320;
    g.gain.setValueAtTime(0.25, t); g.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
    o.connect(g).connect(this.graph!.out);
    o.start(t); o.stop(t + 0.06);
  }

  /** Spitzenpegel 0..1 pro Spur und Master */
  levels(): Record<string, number> {
    const out: Record<string, number> = {};
    if (!this.graph) return out;
    for (const [id, s] of this.graph.strips) {
      s.meter.getFloatTimeDomainData(s.meterBuf as Float32Array<ArrayBuffer>);
      let pk = 0; for (const v of s.meterBuf) pk = Math.max(pk, Math.abs(v));
      out[id] = pk;
    }
    const [l, r] = this.graph.stereoPeak('master');
    out.master = Math.max(l, r);
    return out;
  }
  /** Spitzenpegel links/rechts eines Kanals (Spur, Bus oder 'master') */
  stereo(id: string): [number, number] { return this.graph ? this.graph.stereoPeak(id) : [0, 0]; }
  get loudness() { return this.graph?.loudness; }
  resetLoudness() { this.graph?.resetLoudness(); }
  /** laufende Plugin-Instanz eines Inserts (für Messwerte, EQ-Kurve und Analyzer) */
  plugin(channelId: string, slotId: string) { return this.graph?.channel(channelId)?.chain.instance(slotId); }

  /** Dekodiert Audio (WAV, MP3, AIFF, FLAC …) auf 48 kHz. Braucht keinen Klick, damit es auch beim Drag & Drop geht. */
  async decode(sourceId: string, data: ArrayBuffer) {
    this.decoder ??= new OfflineAudioContext(2, 1, 48000);
    const buf = await (this.ctx ?? this.decoder).decodeAudioData(data);
    this.buffers.set(sourceId, buf);
    peaks(buf);
    return buf;
  }
  private decoder: OfflineAudioContext | null = null;

  /** Note vorhören (MIDI-Editor), über den Kanal der Spur */
  async preview(trackId: string, midi: number, vel = 0.8, dur = 0.35) {
    const ctx = await this.ensure();
    const t = useStudio.getState().project.tracks.find((x) => x.id === trackId);
    if (!t) return;
    voiceFor(t.instrument?.plugin, t.instrument?.preset)(ctx, this.strip(t).input, midi, vel, ctx.currentTime + 0.01, dur);
  }

  /** Offline-Bounce des Mixes (Basis für Referenz-Vergleich, A&R-Export, Lautheitsmessung) */
  /** Konsolidieren: Clips einer Spur im Bereich [s, e] zu einer Audiodatei rendern (Clip Gain, Fades, ohne Inserts und Fader) */
  async renderTrackRange(p: Project, trackId: string, s: number, e: number): Promise<AudioBuffer> {
    await elasticReady(p);
    const off = new OfflineAudioContext(2, Math.max(1, Math.ceil((e - s) * 48000)), 48000);
    for (const seg of audioSegments(p, trackId)) {
      if (seg.to <= s || seg.from >= e) continue;
      const buf = this.buffers.get(seg.sourceId ?? seg.clip.sourceId!);
      if (!buf) continue;
      const a = Math.max(seg.from, s), b = Math.min(seg.to, e), srcAt = seg.src + (a - seg.from);
      if (srcAt >= buf.duration) continue;
      const src = off.createBufferSource(); src.buffer = buf;
      const g = off.createGain(); src.connect(g).connect(off.destination);
      applyEnvelope(g, seg.clip, a, b, (x) => x - s);
      src.start(a - s, srcAt, Math.min(b - a, buf.duration - srcAt));
    }
    return off.startRendering();
  }

  /** Offline rendern, mit derselben Signalkette wie bei der Wiedergabe. from: Startpunkt im Song (Ergebnis beginnt dort). */
  async bounce(p: Project, seconds: number, from = 0, opts: { busInput?: string } = {}): Promise<AudioBuffer> {
    await elasticReady(p);
    const off = new OfflineAudioContext(2, Math.ceil(seconds * 48000), 48000);
    const g = new MixGraph(off, false);
    await g.init();
    g.sync(p, { phone: false, mono: false }, { playing: true, pos: from, manual: new Set() });
    if (opts.busInput) {
      // Nur den Eingang eines Busses rechnen (Summe seiner Sends und der darauf gerouteten Spuren)
      for (const t of p.tracks) { const s = g.strips.get(t.id); if (s && t.output !== opts.busInput) s.out.disconnect(); }
      for (const [id, s] of g.busses) { s.out.disconnect(); if (id === opts.busInput) s.input.connect(off.destination); }
      g.master.out.disconnect();
    }
    const end = from + seconds, ctxT = (x: number) => Math.max(0, x - from);
    for (const t of p.tracks) {
      if (t.kind === 'bus') continue;
      const input = g.strip(t.id).input;
      if (isFrozen(p, t)) {
        const fb = this.buffers.get(t.frozen!.sourceId);
        if (fb && from < fb.duration) { const s = off.createBufferSource(); s.buffer = fb; const fg = off.createGain(); fg.gain.value = Math.pow(10, (t.frozen!.gainDb ?? 0) / 20); s.connect(fg).connect(input); s.start(0, from, Math.min(seconds, fb.duration - from)); }
        continue;
      }
      if (fxRendered(p, t)) {
        const rb = this.buffers.get(t.bridgeRender!.sourceId)!;
        if (from < rb.duration) { const s = off.createBufferSource(); s.buffer = rb; s.connect(input); s.start(0, from, Math.min(seconds, rb.duration - from)); }
        continue;
      }
      for (const seg of audioSegments(p, t.id)) {
        const buf = this.buffers.get(seg.sourceId ?? seg.clip.sourceId!);
        if (!buf || seg.from >= end || seg.to <= from) continue;
        const a = Math.max(seg.from, from), src = seg.src + (a - seg.from);
        if (src >= buf.duration) continue;
        const s = off.createBufferSource(); s.buffer = buf;
        const fg = off.createGain(); s.connect(fg).connect(input);
        applyEnvelope(fg, seg.clip, a, seg.to, ctxT);
        s.start(a - from, src, Math.min(seg.to - a, buf.duration - src));
      }
      const v = voiceFor(t.instrument?.plugin, t.instrument?.preset);
      for (const c of p.clips) if (c.trackId === t.id && c.kind === 'midi') for (const e of midiEvents(p, c, from, end)) if (e.at >= from) v(off, input, e.n.m, e.n.v, e.at - from, e.dur);
    }
    if (!opts.busInput) for (const b of bussesOf(p)) if (fxRendered(p, b)) {
      const rb = this.buffers.get(b.bridgeRender!.sourceId)!, ch = g.channel(b.id);
      if (ch && from < rb.duration) { const s = off.createBufferSource(); s.buffer = rb; s.connect(ch.chain.input); s.start(0, from, Math.min(seconds, rb.duration - from)); }
    }
    g.scheduleAutomation(p, from, ctxT, new Set());
    // Plugin-Parameter-Automation: das Rendern alle 10 ms kurz anhalten und die Werte setzen
    const insAuto = [...p.tracks, masterTrack(p)].some((t) => insOverrides(t, from));
    if (insAuto) for (let t = 0.01; t < seconds; t += 0.01) { const at = t; void off.suspend(at).then(() => { g.applyInsAutomation(p, from + at, new Set()); void off.resume(); }); }
    return off.startRendering();
  }

  stripOf(trackId: string) { return this.graph?.strips.get(trackId); }
}

export const engine = new Engine();
fx.has = (id) => engine.buffers.has(id);
// Elastic Audio: sobald gerechnete Stücke fertig sind, während der Wiedergabe neu einplanen (gebündelt)
let elTimer: ReturnType<typeof setTimeout> | null = null;
onElasticChange(() => { if (elTimer) return; elTimer = setTimeout(() => { elTimer = null; if (useStudio.getState().transport.playing) engine.reschedule(); }, 120); });

// Projektänderungen sofort hörbar machen
useStudio.subscribe((s, prev) => {
  if (s.project === prev.project) return;
  engine.sync(s.project);
  if (s.transport.playing && audioSig(s.project) !== audioSig(prev.project)) engine.reschedule();
  if (s.transport.playing && autoSig(s.project) !== autoSig(prev.project)) engine.rescheduleAutomation();
});

const autoCache = new WeakMap<object, string>();
function autoSig(p: Project): string {
  let x = autoCache.get(p.tracks);
  if (x === undefined) { x = JSON.stringify(p.tracks.map((t) => t.automation ?? null)); autoCache.set(p.tracks, x); }
  return x + JSON.stringify(p.masterAutomation ?? null);
}

/** Signatur der klangrelevanten Audio-Clip-Daten (nur bei Änderungen daran wird neu eingeplant) */
const sigCache = new WeakMap<object, string>();
function audioSig(p: Project): string {
  let x = sigCache.get(p.clips);
  if (x === undefined) {
    x = p.clips.filter((c) => c.kind === 'audio').map((c) => [c.id, c.trackId, c.start, c.duration, c.offset, c.sourceId, c.gainDb ?? 0, c.muted ? 1 : 0, c.fadeIn, c.fadeOut, c.fadeInShape ?? '', c.fadeOutShape ?? '', c.loop?.length ?? 0, c.warp ? JSON.stringify(c.warp) : ''].join(',')).join(';');
    sigCache.set(p.clips, x);
  }
  // Einfrieren/Auftauen und Elastic-Modus ändern ebenfalls, was klingt
  return x + '|' + p.tracks.map((t) => `${t.frozen && isFrozen(p, t) ? t.frozen.sourceId : ''}:${t.elastic ?? ''}`).join(',');
}
